/**
 * newjourneys-api — the persistence seam for unified Journey documents and
 * Experiences (rail "New journeys").
 *
 * LOCAL PATCH (platform side, 2026-09-08, JAI-1512): the contract this module
 * was written to be swapped onto has landed — `packages/api-contracts/src/newjourneys.ts`
 * declares the eight ops under /v1/admin/newjourneys, `@v0y4ge/api-client` wraps
 * them (`listJourneyDocuments`, `putJourneyDocument`, `deleteJourneyDocument`,
 * `listExperiences`, `putExperience`, `deleteExperience`, …) and the worker
 * serves them. The pack still shipped this file resolving every call from a
 * per-tenant localStorage envelope marked `__source: "cd-local-stub"`, so a
 * journey designed in one browser did not exist in another and never reached
 * the tenant. Every function below now calls the typed client; the signatures
 * are unchanged so the screens do not move. The `slug` parameter is kept for
 * the callers' sake — the tenant is the session's, resolved by the client.
 *
 * IMPORTANT: documents here are OVERLAYS, never the whole list. The journeys
 * list derives its rows from the tenant's real queues, events and appointment
 * catalogue (the objects that own the journey); an overlay carries only what an
 * administrator changed in the designer, keyed by the derived row's stable key
 * (`queue:<id>`, `event:<id>`, `appt:<lobId>`), plus any journey created from a
 * template (`local:<uid>`), which has no owning object yet.
 */
import type { JourneyDocumentPutInput, ExperiencePutInput } from "@v0y4ge/api-contracts";
import {
  listJourneyDocuments,
  putJourneyDocument,
  deleteJourneyDocument,
  listExperiences,
  putExperience as putExperienceOp,
  deleteExperience as deleteExperienceOp,
} from "@v0y4ge/api-client";

export interface NJStoreEnvelope {
  documents: Record<string, unknown>;
  experiences: Record<string, unknown>;
  __source?: string;
}

/** The served page size cap (contract: limit <= 200). */
const PAGE = 200;

/** The contract requires a non-empty `name` (`JourneyDocumentDto` and
 *  `ExperienceDto` both `.min(1)` it). Every designer path that creates a
 *  document sets one (`templateDoc`, duplicate, attach); the edit path can
 *  blank it, because the title inputs autosave on every keystroke. Storing the
 *  key as the name for that keystroke beats the alternative — a 422 the screen
 *  never sees (its writes are fire-and-forget), repeated on every autosave
 *  until a title is typed again. */
function named<T extends { name?: unknown }>(value: T, key: string): T & { name: string } {
  const name = typeof value.name === "string" && value.name.trim() ? value.name : key;
  return { ...value, name };
}

/** Walk a paginated list op, up to 50 pages of 200 (10,000 rows); an overlay
 *  store past that is truncated silently, which no tenant is near. */
async function allPages<T extends { key: string }>(
  list: (input: { page: number; limit: number }) => Promise<{ data: T[]; pagination: { page: number; total_pages?: number; total: number } }>,
): Promise<T[]> {
  const out: T[] = [];
  for (let page = 1; page <= 50; page++) {
    const res = await list({ page, limit: PAGE });
    out.push(...res.data);
    const pages = res.pagination.total_pages ?? Math.ceil(res.pagination.total / PAGE);
    if (page >= pages || res.data.length === 0) break;
  }
  return out;
}

/** GET /v1/admin/newjourneys/documents + /experiences — document overlays + experiences for the tenant. */
export async function listJourneyStore(_slug: string): Promise<NJStoreEnvelope> {
  const [docs, exps] = await Promise.all([allPages(listJourneyDocuments), allPages(listExperiences)]);
  const documents: Record<string, unknown> = {};
  for (const { key, ...doc } of docs) documents[key] = doc;
  const experiences: Record<string, unknown> = {};
  for (const { key, ...exp } of exps) experiences[key] = exp;
  return { documents, experiences, __source: "served" };
}

/** PUT /v1/admin/newjourneys/documents/{key} */
export async function putJourneyDoc(_slug: string, key: string, doc: unknown): Promise<void> {
  // The screen types its document `unknown`; the contract's `.passthrough()`
  // input is the shape the worker validates (422 on a miss), so that is the cast.
  await putJourneyDocument(key, named((doc ?? {}) as Record<string, unknown>, key) as JourneyDocumentPutInput);
}

/** DELETE /v1/admin/newjourneys/documents/{key} — drops the overlay only; a
 *  derived row returns to whatever its owning queue / event / catalogue says. */
export async function deleteJourneyDoc(_slug: string, key: string): Promise<void> {
  await deleteJourneyDocument(key);
}

/** PUT /v1/admin/newjourneys/experiences/{key} */
export async function putExperience(_slug: string, key: string, exp: unknown): Promise<void> {
  await putExperienceOp(key, named((exp ?? {}) as Record<string, unknown>, key) as ExperiencePutInput);
}

/** DELETE /v1/admin/newjourneys/experiences/{key} */
export async function deleteExperience(_slug: string, key: string): Promise<void> {
  await deleteExperienceOp(key);
}

/** False: the ops above are served, not stubbed. The screen's only reader
 *  (NewJourneys.tsx, the journeys-list footer) drops one sentence of help copy
 *  when this is false. */
export const NJ_STUBBED = false;
